import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import HttpCheckoutRepository from "../../../../../../api/request/Checkout/Checkout.service";
import HttpLeadRepository from "../../../../../../api/request/Lead/Lead.service";
import { GetlifeContext } from "../../../../../../context/GetLifeContext.context";
import { I18nContext } from "../../../../../../context/i18n.context";
import * as PDFS_ROUTES from "../../../../../../assets/pdfs";
import { GetLifeTrackerContext } from "../../../../../../context/GetLifeTracker.context";
import { StatusContext } from "../../../../../../context/StatusContext";
import {
  track,
  tracker,
  trackPaymentFailed,
  trackSubmissionError,
} from "../../../../../../helpers/Tracking";
import { DOMAIN } from "../../../../../../constants/Global";
import { annualizePremium, CONVERSION_CURRENCY } from "../../../../../../utils/Utils";
import { formatPhoneWithCallCode } from "../../../../../../utils/InternationlChanges";
import { hashIdentity } from "../../../../../../utils/hashIdentity";
import { NewCheckoutI } from "../../../../../../api/request/Lead/Interfaces/DataInterface.response";
import { isNativeMode } from "../../../../../../native/nativeMode";
import {
  ProposalFailureKindT,
  proposalOutcome,
} from "../../../../../../native/proposalOutcome";
import { LegalNoteRejectionI, legalNoteRejection } from "./legalNoteRejection";

type userAcceptanceT = "privacyPolicies" | "conditions" | "personalData";

/** Datos anagráficos que el cliente puede corregir tras un FAILURE de la proposal. */
export interface AnagraphicFormI {
  name: string;
  lastName: string;
  idNumber: string;
  cityOfBirth: string;
  nationality: string;
  /** Solo lectura: cambiarlas cambia la tarifa, no se editan aquí. */
  birthDate: string;
  gender: string;
}

// Reintento acotado de ensure-funds: tope de intentos y espera entre ellos, para
// no machacar el servicio si falla de forma persistente (ver ensureFundsFromCustomer).
const MAX_ENSURE_FUNDS_RETRIES = 3;
const ENSURE_FUNDS_RETRY_DELAY_MS = 1500;

const CheckoutPayController = () => {
  const {
    setLoading,
    utm_campaign,
    utm_source,
    utm_medium,
    utm_content,
    utm_term,
    utm_test,
    gclid,
    gbraid,
    wbraid,
    msclkid,
    fbclid,
    getEntryPage,
    getReferrerUrl,
    getABTestVersion,
    token,
    leadId, domain
  } = useContext(GetlifeContext);

  const {
    state: { translate },
  } = useContext(I18nContext);
  const { getStatus } = useContext(StatusContext);

  const { handleTrackerQuestion } = useContext(GetLifeTrackerContext);

  const [note, setNote] = useState<string>();
  const [noteUrl, setNoteURL] = useState<string>();
  const [activeButton, setActiveButton] = useState(false);

  const navigate = useNavigate();

  const leadRepository = new HttpLeadRepository(leadId, token);
  const checkoutRepository = new HttpCheckoutRepository(leadId, token);

  const navigateTo = () => navigate("/payment/5");

  const [modalShow, setModalShow] = useState(false);

  const [confirmed, setConfirmed] = useState(false);
  const handleChangeConfirmed = () => {
    setConfirmed(!confirmed);
  };
  const userHasConditionsToAccept = DOMAIN === "it";
  const [userAcceptance, setUserAcceptance] = useState<{
    [key in userAcceptanceT]: boolean;
  }>({
    privacyPolicies: false,
    conditions: false,
    personalData: false,
  });

  const [checkout, setCheckout] = useState<NewCheckoutI>();

  // FAILURE de la proposal native: el cliente ve el motivo y puede corregir sus
  // datos y reintentar, en vez de seguir a ensure-funds (404 sin suscripción).
  const [proposalFailure, setProposalFailure] =
    useState<ProposalFailureKindT | null>(null);
  const [anagraphic, setAnagraphic] = useState<AnagraphicFormI | null>(null);
  // 409 de accept-legal-note (POLICY_VERSION_NOT_SELLABLE): la nota NO se ha
  // aceptado; se avisa aquí en vez de ir a /waitingvalidation.
  const [legalNoteRejected, setLegalNoteRejected] =
    useState<LegalNoteRejectionI | null>(null);

  const loadAnagraphic = async () => {
    try {
      const { lead } = (await leadRepository.getCheckoutData()) as any;
      setAnagraphic({
        name: lead?.name ?? "",
        lastName: lead?.lastName ?? "",
        idNumber: lead?.idNumber ?? "",
        cityOfBirth: lead?.cityOfBirth ?? "",
        nationality: lead?.nationality ?? "",
        birthDate: lead?.birthdate ?? "",
        gender: lead?.gender ?? "",
      });
    } catch {
      setAnagraphic(null);
    }
  };

  const showProposalFailure = (kind: ProposalFailureKindT) => {
    setLoading(false);
    setActiveButton(true);
    setProposalFailure(kind);
    void loadAnagraphic();
  };

  const acceptLegalNoteNative = async () => {
    let response: unknown;
    try {
      response = await checkoutRepository.acceptLegalNote();
    } catch (e) {
      trackSubmissionError(e);
      showProposalFailure("GENERIC");
      return;
    }
    const outcome = proposalOutcome(response);
    if (!outcome.ok) {
      trackSubmissionError(new Error(`proposal ${outcome.kind}`), {
        error_type: "proposal",
        code: outcome.kind,
      });
      showProposalFailure(outcome.kind);
      return;
    }
    track("legal_note_accepted", {});
    setProposalFailure(null);
    await sendTrack();
    await isReadyToPaymentValidation();
  };

  /** Guarda los datos corregidos (agl_insured vía gateway) y reintenta la proposal. */
  const saveAnagraphicAndRetry = async (form: AnagraphicFormI) => {
    setLoading(true);
    try {
      await leadRepository.saveData({
        name: form.name.trim(),
        lastName: form.lastName.trim(),
        idNumber: form.idNumber.replace(/\s/g, "").toUpperCase(),
        cityOfBirth: form.cityOfBirth,
      });
    } catch (e) {
      trackSubmissionError(e);
      setLoading(false);
      return false;
    }
    setProposalFailure(null);
    handleRequest();
    return true;
  };

  const retryProposal = () => {
    setProposalFailure(null);
    handleRequest();
  };

  const acceptLegalNote = async () => {
    if (isNativeMode()) {
      return acceptLegalNoteNative();
    }
    const responseData = await checkoutRepository.acceptLegalNote().then(async () => {
      track("legal_note_accepted", {});
      await sendTrack();
      await isReadyToPaymentValidation()
    }).catch((e) => {
      trackSubmissionError(e);
      setLoading(false);
      const rejection = legalNoteRejection(e);
      if (null !== rejection) {
        setActiveButton(true);
        setLegalNoteRejected(rejection);
        return;
      }
      navigate("/waitingvalidation");
    });

    return responseData
  }

  const isReadyToPaymentValidation = async () => {
    await checkoutRepository.isReadyToPaymentValidation().then(async () => {
      await ensureFundsFromCustomer()
    }).catch((e) => {
      trackSubmissionError(e);
      setLoading(false);
      navigate("/waitingvalidation");

    });
  }

  const ensureFundsFromCustomer = async (attempt = 0): Promise<void> => {
    await checkoutRepository.ensureFundsFromCustomer().then((response) => {
      if (response.status === "SUCCESS") {
        track("funds_verified", {});
        setLoading(false);
        return navigateTo();
      }

      if (response.status === "VALIDATION_PENDING") {
        track("funds_verification_pending", {});
        setLoading(false);
        navigate("/waitingvalidation");
      }

      if (response.status === "3DS_PENDING") {
        // Se sale a la página del banco: page_exited dirá redirect a 3ds, y lo
        // pendiente sale ya con beacon.
        track("threeds_requested", { stage: "ensure_funds" });
        tracker.redirecting("3ds");
        window.location.href = response.secureUrl;
      }

    }).catch((e) => {
      // Reintento ACOTADO. Antes se re-llamaba sin límite ni delay: si ensure-funds
      // fallaba de forma persistente (p.ej. 404 del endpoint native en ms-payment)
      // el front machacaba el servicio en bucle. Máx. intentos con espera; agotados
      // → /waitingvalidation (mismo destino que el resto de catch del flujo).
      if (attempt + 1 >= MAX_ENSURE_FUNDS_RETRIES) {
        // Fallo del cobro: solo payment_failed (antes también contaba como error de envío).
        trackPaymentFailed("ensure_funds", e);
        setLoading(false);
        navigate("/waitingvalidation");
        return;
      }
      window.setTimeout(() => {
        void ensureFundsFromCustomer(attempt + 1);
      }, ENSURE_FUNDS_RETRY_DELAY_MS);
    })

  }

  const handleRequest = () => {
    setActiveButton(false);
    setLoading(true);
    acceptLegalNote();
  }

  const sendTrack = async () => {
    const event = "policyPurchased";

    const leadInfo = await leadRepository.getInfoLead();
    const identity = await hashIdentity({
      email: leadInfo?.email,
      phone: formatPhoneWithCallCode(leadInfo?.phone),
    });

    const eventData = {
      ...identity,
      page: window.location.href,
      leadId: leadId,
      calculatedCapital: leadInfo?.capitals?.DEATH?.capital ?? checkout!.capital,
      coverage: checkout!.coverage === "premium" ? "FIPA" : "F",
      intent: checkout!.intention,
      calculatedPremium: annualizePremium(checkout!.regularPrice, checkout!.period),
      paymentMethod: "card",
      utm_source: utm_source,
      utm_campaign: utm_campaign,
      utm_medium: utm_medium,
      utm_content: utm_content,
      utm_term: utm_term,
      utm_test: utm_test,
      gclid: gclid,
      gbraid: gbraid,
      wbraid: wbraid,
      msclkid: msclkid,
      fbclid: fbclid,
      platform: "App",
      discountedPremium: annualizePremium(
        checkout!.promotionalCode ? checkout!.priceToPay : checkout!.regularPrice,
        checkout!.period
      ),
      paymentPeriod: checkout!.period ? checkout!.period.toLowerCase() : "monthly",
      currency: CONVERSION_CURRENCY,
      policyId: leadId,
      entryPage: getEntryPage(),
      referrerUrl: getReferrerUrl(),
      ABTestVersion: getABTestVersion(),
    };

    handleTrackerQuestion({
      type: "GA",
      data: {
        event,
        ...eventData,
      },
    });

    handleTrackerQuestion({
      type: "FS",
      event,
      data: eventData,
    });
  }

  const normalizeNote = (url: string) => {
    const notePDF: string = url.split("/").pop() as string;
    const pdf = notePDF.split(".")[0].replaceAll("-", "_");
    return PDFS_ROUTES[pdf as PDFS_ROUTES.PDFST];
  };

  const fetchNote = async () => {
    const response = await leadRepository.getCheckoutNote();
    setNoteURL(response.url);
    const pdfNote = await normalizeNote(response.url);
    setNote(pdfNote);
    setLoading(false);
  };

  useEffect(() => {
    leadRepository.getPaymentData().then((response: NewCheckoutI) => {
      setCheckout(response);
    });
  }, []);

  useEffect(() => {
    (async () => {
      getStatus();
      await fetchNote();
    })();
  }, []);

  useEffect(() => {
    if (!userHasConditionsToAccept) {
      setTimeout(() => setActiveButton(true), 3000);
    }
  }, [userHasConditionsToAccept]);

  useEffect(() => {
    if (userHasConditionsToAccept) {
      const isPendingToAcceptConditions = Object.keys(userAcceptance).some(
        (key) => userAcceptance[key as userAcceptanceT] === false
      );

      if (isPendingToAcceptConditions) {
        setActiveButton(false);
      } else {
        setActiveButton(true);
      }
    }
  }, [userAcceptance, userHasConditionsToAccept]);

  return {
    activeButton,
    confirmed,
    handleChangeConfirmed,
    handleRequest,
    modalShow,
    note,
    noteUrl,
    setModalShow,
    translate,
    userAcceptance,
    setUserAcceptance,
    userHasConditionsToAccept,
    domain,
    proposalFailure,
    anagraphic,
    saveAnagraphicAndRetry,
    retryProposal,
    closeProposalFailure: () => setProposalFailure(null),
    legalNoteRejected,
    closeLegalNoteRejected: () => setLegalNoteRejected(null),
    leadRepository,
  };
};

export default CheckoutPayController;
